/**
 * A curated walk coming onto a day with its places (Kurtis 2026-10-31). Every
 * add of one — the Map tab's walk card, the walk's own page, the Add sheet's
 * saved walk, a saved walk dragged onto a day — brings the places picked as
 * stops inside it: its numbered stops and its two ends are picked to begin
 * with, the landmarks it passes are not (`pickedByDefault`; the add sheets
 * show the list, the drag and the Add sheet take the default).
 *
 * Each place is a quick stop laid out along the walk's line at a stroll's
 * pace (utils/dayWalks), from the time picked — or, with only a part of the
 * day picked, from that part's first free minute. Never untimed: an untimed
 * stop can't be a quick stop, and would read as an hour each.
 *
 * Pure — no React, no i18n, no clock.
 */
import { walkPlaces } from './walkLabels';
import { layOutPlaces, walkLegMinutes, walkLineOf, walkMinutesTo, walkSpanOf, spanFields } from './dayWalks';
import { BLOCK_WINDOWS, daySpans, suggestStart, toMinutes } from './daySlots';
import { createActivityFromItem } from './activityUtils';

const DAY = 24 * 60;

/**
 * A curated walk's places as add items, in walking order (our places only):
 * { id, placeId, name, title, coordinates, image_url, pickedByDefault } —
 * picked to begin with when it is a numbered stop or one of the walk's ends.
 */
export function walkPlaceItems(walk) {
  return walkPlaces(walk || {}).filter(p => p.attractionId).map(p => ({
    id: p.attractionId,
    placeId: p.attractionId,
    name: p.name,
    title: p.name,
    coordinates: Number.isFinite(p.lat) && Number.isFinite(p.lng) ? { lat: p.lat, lng: p.lng } : null,
    image_url: p.photoUrl || null,
    pickedByDefault: !!(p.number || p.role)
  }));
}

/** The places an add brings when nobody picked: the walk's stops and ends. */
export const defaultWalkPlaces = (items) => (items || []).filter(i => i.pickedByDefault);

/**
 * The picked places (`picked`: a Set of ids) in the order they'll be walked:
 * as the walk lists them, or from its other end (`fromEnd`, Kurtis
 * 2026-11-02 — a walk has no right way round). walkWithPlaces lays them out
 * along the line in this order.
 */
export function pickedInWalkingOrder(items, picked, fromEnd = false) {
  const list = (items || []).filter(i => picked.has(i.id));
  return fromEnd ? list.reverse() : list;
}

/**
 * Whether the picked places can be walked from the walk's other end: two or
 * more, different ends, and — a curated walk — far enough apart along its
 * line to read that way round (dayWalks.walkLineOf; not only a loop's
 * start/finish), or every reader would lay its line's ends on the wrong side.
 */
export function canStartFromEnd(walk, items, picked) {
  const list = pickedInWalkingOrder(items, picked);
  if (list.length < 2 || list[0].id === list[list.length - 1].id) return false;
  const path = walk?.walkPath;
  if (!Array.isArray(path) || path.length < 2) return true;
  return walkLineOf(walk, list) === path && walkLineOf(walk, list.slice().reverse()) !== path;
}

/**
 * Where a walk added to a day starts (day-frame minutes): the time picked
 * (`late`: a small-hours time on this day's night out), else the first free
 * minute in the part of the day picked (utils/daySlots' one start rule, kept
 * inside that part), else — when a flight or train fills that part — the
 * minute it arrives, else that part's start.
 */
export function walkStartMinute({ scheduleTime = null, late = false, timeBlock = null, day = null, transports = [], coords = null, minutes = 60 } = {}) {
  const at = toMinutes(scheduleTime);
  if (at !== null) return at + (late ? DAY : 0);
  const [from, to] = BLOCK_WINDOWS[timeBlock] || BLOCK_WINDOWS.morning;
  const spans = day ? daySpans(day, transports) : [];
  const sg = day ? suggestStart(spans, minutes, coords, { dayStart: from, dayEnd: to, minGap: 15 }) : null;
  if (sg && sg.minutes >= from && sg.minutes < to) return sg.minutes;
  // Never on a plane: after the transport the part of the day starts on.
  // (One leaving and not arriving today runs to midnight: no start after it.)
  const riding = spans.filter(s => s.kind === 'transport' && s.start <= from && s.end > from && s.end < DAY);
  return riding.length ? Math.ceil(Math.max(...riding.map(s => s.end)) / 5) * 5 : from;
}

/**
 * A curated walk and its picked places as the stops to add, as one change:
 * [walk, ...places]. Each place is a quick stop inside the walk, laid out from
 * the walk's start (the stroll from the line's start to the first place
 * first); the walk holds its places' span (walkSpanOf, as the planner's
 * funnel keeps it), so a save outside the planner stores the same. With no
 * places — or "All day" and no time, which gives them no minute to be at —
 * the walk goes alone, at the time picked.
 *
 * @param {object} walk - the walk stop (utils/mapTours walkActivity)
 * @param {object[]} items - its picked places (walkPlaceItems), in the order
 *   walked: as the walk lists them, or reversed to start from its other end
 *   (pickedInWalkingOrder)
 * @param {object} opts - { day, transports, scheduleTime, timeBlock, mintId }
 */
export function walkWithPlaces(walk, items, { day = null, transports = [], scheduleTime = null, timeBlock = null, mintId } = {}) {
  const { walkPlaceItems: _items, bringWalkPlaces: _bring, ...stop } = walk || {};
  const walkId = stop.clientActivityId || mintId();
  const block = timeBlock || stop.timeBlock || null;
  if (!items?.length || (!scheduleTime && block === 'all_day')) return [{ ...stop, clientActivityId: walkId, scheduledTime: scheduleTime || null, ...(block ? { timeBlock: block } : {}) }];
  // A small-hours time on an evening is the tail of this day's night out.
  const late = !!scheduleTime && scheduleTime < '06:00' && block === 'evening';
  const places = items.map(({ pickedByDefault: _p, ...item }) => ({
    ...createActivityFromItem(item), clientActivityId: mintId(), inWalk: walkId, quickStop: true, duration: null
  }));
  const firstPlaced = places.find(p => p.coordinates);
  const start = walkStartMinute({
    scheduleTime, late, timeBlock: block, day, transports,
    coords: firstPlaced?.coordinates || stop.coordinates || null,
    minutes: Number(stop.duration) > 0 ? Number(stop.duration) : 60
  });
  // Along the line the way the places are given: from its other end when they run back along it.
  const line = walkLineOf(stop, places);
  const lead = firstPlaced ? walkMinutesTo(line, firstPlaced) : 0;
  const laid = layOutPlaces(start + lead, places, { legMinutes: walkLegMinutes(line, places) });
  const timed = places.map((p, i) => ({ ...p, ...laid[i].patch }));
  const own = { ...stop, timeBlock: scheduleTime ? (late ? 'evening' : null) : block };
  const span = walkSpanOf(own, timed);
  return [{ ...own, clientActivityId: walkId, ...(span ? spanFields(span, own) : { scheduledTime: scheduleTime || null }) }, ...timed];
}
